import { Controller } from "@hotwired/stimulus"

// Dropdown for the shared filter panel. Unlike the generic `dropdown` controller
// (which portals its menu to <body>), this keeps the menu inside the toolbar DOM
// so the filter-persistence controller and the in-menu Reset button keep working.
// To escape the table card's `overflow-hidden` (which clips the menu when the
// table is short), the open menu is positioned with `position: fixed` — fixed
// elements are not clipped by non-transformed overflow ancestors — anchored to
// the toggle button, clamped to the viewport, and flipped above when needed.
export default class extends Controller {
  static targets = ["menu"]

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    this.onReposition = this.reposition.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    this.stopTracking()
  }

  toggle(event) {
    event?.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.reposition()
    window.addEventListener("resize", this.onReposition)
    window.addEventListener("scroll", this.onReposition, true)
    document.addEventListener("keydown", this.onKeydown)
  }

  close() {
    const menu = this.menuTarget
    menu.classList.add("hidden")
    menu.style.position = ""
    menu.style.top = ""
    menu.style.bottom = ""
    menu.style.left = ""
    menu.style.right = ""
    menu.style.marginTop = ""
    menu.style.maxHeight = ""
    menu.style.overflowY = ""
    this.stopTracking()
  }

  reposition() {
    const menu = this.menuTarget
    const button = this.element.querySelector("button") || this.element
    const rect = button.getBoundingClientRect()
    const gap = 4
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight

    menu.style.position = "fixed"
    menu.style.marginTop = "0"

    // Horizontal: align the menu's left edge to the button, clamped so it never
    // spills past either viewport edge.
    const width = menu.offsetWidth
    const left = Math.max(margin, Math.min(rect.left, vw - width - margin))
    menu.style.left = `${left}px`
    menu.style.right = "auto"

    // Vertical: below the button, flipping above when there is more room there.
    const spaceBelow = vh - rect.bottom - margin
    const spaceAbove = rect.top - margin
    if (spaceBelow < menu.offsetHeight && spaceAbove > spaceBelow) {
      menu.style.top = "auto"
      menu.style.bottom = `${vh - rect.top + gap}px`
      menu.style.maxHeight = `${spaceAbove - gap}px`
    } else {
      menu.style.top = `${rect.bottom + gap}px`
      menu.style.bottom = "auto"
      menu.style.maxHeight = `${spaceBelow - gap}px`
    }
    menu.style.overflowY = "auto"
  }

  handleDocumentClick(event) {
    const insidePortaledSelect = event.target.closest?.(".ts-dropdown")
    if (this.isOpen && !this.element.contains(event.target) && !insidePortaledSelect) this.close()
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  stopTracking() {
    window.removeEventListener("resize", this.onReposition)
    window.removeEventListener("scroll", this.onReposition, true)
    document.removeEventListener("keydown", this.onKeydown)
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }
}
